import { clsx } from 'clsx';
import { useCanvas } from '../state/canvas-store.js';
import type { FillStyle, Roughness, StrokeStyle } from '@extapia/shared';

const STROKE_COLORS = ['#111827', '#e11d48', '#ea580c', '#ca8a04', '#16a34a', '#0284c7', '#7c3aed', '#db2777'];
const FILL_COLORS = ['transparent', '#fca5a5', '#fdba74', '#fde68a', '#86efac', '#93c5fd', '#c4b5fd', '#f9a8d4'];
const WIDTHS = [1, 2, 4] as const;
const STROKE_STYLES: StrokeStyle[] = ['solid', 'dashed', 'dotted'];
const FILL_STYLES: FillStyle[] = ['solid', 'hachure', 'cross-hatch'];
const ROUGHNESS: Roughness[] = [0, 1, 2];

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-[11px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{label}</span>
    <div className="flex items-center gap-1.5 flex-wrap">{children}</div>
  </div>
);

const Swatch = ({ color, selected, onClick }: { color: string; selected: boolean; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className={clsx(
      'w-6 h-6 rounded-md border transition-all',
      selected ? 'ring-2 ring-sky-500 ring-offset-1 ring-offset-white dark:ring-offset-neutral-900' : 'border-neutral-300 dark:border-neutral-700',
      color === 'transparent' ? 'bg-[conic-gradient(#e5e7eb_0deg,#fff_90deg,#e5e7eb_180deg,#fff_270deg)]' : '',
    )}
    style={color !== 'transparent' ? { backgroundColor: color } : {}}
    aria-label={color}
  />
);

const Pill = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={clsx(
      'px-2 h-7 rounded-md text-xs font-medium transition-colors',
      active
        ? 'bg-sky-500 text-white'
        : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-200 dark:hover:bg-neutral-700',
    )}
  >
    {children}
  </button>
);

export const StylePanel = () => {
  const app = useCanvas((s) => s.app);
  const setActiveStyle = useCanvas((s) => s.setActiveStyle);

  return (
    <aside className="absolute top-4 left-4 z-10 w-56 rounded-xl bg-white/95 dark:bg-neutral-900/95 backdrop-blur shadow-lg ring-1 ring-black/5 p-3 flex flex-col gap-3">
      <div className="text-xs font-semibold text-neutral-800 dark:text-neutral-100">Style</div>

      <Row label="Stroke">
        {STROKE_COLORS.map((c) => (
          <Swatch key={c} color={c} selected={app.activeStrokeColor === c} onClick={() => setActiveStyle({ activeStrokeColor: c })} />
        ))}
      </Row>

      <Row label="Fill">
        {FILL_COLORS.map((c) => (
          <Swatch key={c} color={c} selected={app.activeFillColor === c} onClick={() => setActiveStyle({ activeFillColor: c })} />
        ))}
      </Row>

      <Row label="Stroke width">
        {WIDTHS.map((w) => (
          <Pill key={w} active={app.activeStrokeWidth === w} onClick={() => setActiveStyle({ activeStrokeWidth: w })}>
            {w}px
          </Pill>
        ))}
      </Row>

      <Row label="Style">
        {STROKE_STYLES.map((s) => (
          <Pill key={s} active={app.activeStrokeStyle === s} onClick={() => setActiveStyle({ activeStrokeStyle: s })}>
            {s}
          </Pill>
        ))}
      </Row>

      <Row label="Fill style">
        {FILL_STYLES.map((s) => (
          <Pill key={s} active={app.activeFillStyle === s} onClick={() => setActiveStyle({ activeFillStyle: s })}>
            {s}
          </Pill>
        ))}
      </Row>

      <Row label="Sloppiness">
        {ROUGHNESS.map((r) => (
          <Pill key={r} active={app.activeRoughness === r} onClick={() => setActiveStyle({ activeRoughness: r })}>
            {r === 0 ? 'clean' : r === 1 ? 'rough' : 'wild'}
          </Pill>
        ))}
      </Row>

      <Row label="Opacity">
        <input
          type="range"
          min={0}
          max={100}
          value={app.activeOpacity}
          onChange={(e) => setActiveStyle({ activeOpacity: Number(e.target.value) })}
          className="w-full accent-sky-500"
        />
        <span className="text-xs text-neutral-500 dark:text-neutral-400 min-w-8 text-right">{app.activeOpacity}</span>
      </Row>
    </aside>
  );
};
